/** Brand KPI hero cards (metrics 9, 10 & 29). */
import { getIntlLocale } from "util/locale";

export const WELCOME_BRAND_KPI_HERO_METRIC_IDS = new Set([9, 10, 29]);

/** Metrics whose topic-tab review_count pill opens the reviews drawer (widget scope). */
export const WELCOME_WIDGET_SCOPE_REVIEW_METRIC_IDS = new Set([8, 9, 10, 21, 29]);

export function welcomeWidgetUsesBrandKpiHero(widget) {
  const mid = Number(widget?.metric_id);
  return WELCOME_BRAND_KPI_HERO_METRIC_IDS.has(mid);
}

/** Metrics 10 & 29 hero include breakdown + mixed-inner — needs two grid columns on desktop. */
export function welcomeWidgetUsesDoubleGridColumn(widget) {
  const mid = Number(widget?.metric_id);
  return mid === 10 || mid === 29;
}

/** Numeric hero value from API header.value or legacy title string. */
export function resolveBrandKpiHeroValue(header, metricId) {
  if (!header || typeof header !== "object") return null;
  const raw = header.value;
  if (raw != null && raw !== "") {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  const title = String(
    header.legacy_title ?? header.title ?? "",
  ).trim();
  const mid = Number(metricId);
  if (mid === 9) {
    const m = /overall\s+average\s+rating\s+([\d.]+)/i.exec(title);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n)) return n;
    }
  }
  if (mid === 10) {
    const m = /overall\s+sentiment\s+score\s+([\d.]+)/i.exec(title);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n)) return n;
    }
  }
  if (mid === 29) {
    const m = /overall\s+praise\s+intent\s+score\s+([\d.]+)/i.exec(title);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

/** Hero header from chart/bar payload, topic_tabs General tab, or legacy list items row. */
function resolveBrandKpiHeroHeaderFromTopicTabs(data, metricId) {
  const tabs = data?.topic_tabs;
  if (!Array.isArray(tabs) || tabs.length === 0) {
    return null;
  }
  const generalTab = tabs.find((tab) => tab?.id === "general");
  const orderedTabs = [
    ...(generalTab ? [generalTab] : []),
    ...tabs.filter((tab) => tab && tab !== generalTab),
  ];
  for (const tab of orderedTabs) {
    if (!tab || tab.payload_pending || tab.payload == null) {
      continue;
    }
    const header = resolveBrandKpiHeroHeader(tab.payload, metricId);
    if (header != null) {
      return header;
    }
  }
  return null;
}

export function resolveBrandKpiHeroHeader(data, metricId) {
  const mid = Number(metricId);
  if (!WELCOME_BRAND_KPI_HERO_METRIC_IDS.has(mid) || !data || typeof data !== "object") {
    return null;
  }

  if (data.header && typeof data.header === "object") {
    const value = resolveBrandKpiHeroValue(data.header, mid);
    if (value != null && Number.isFinite(value)) {
      return data.header;
    }
  }

  const fromTopicTabs = resolveBrandKpiHeroHeaderFromTopicTabs(data, mid);
  if (fromTopicTabs != null) {
    return fromTopicTabs;
  }

  const items = data.items;
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }
  const item = items[0];
  if (!item || typeof item !== "object") {
    return null;
  }

  const title = String(item.title ?? "").trim();
  const descRaw = item.description;
  const descNum =
    descRaw != null && descRaw !== "" ? Number(descRaw) : NaN;

  if (mid === 9 && /overall\s+average\s+rating/i.test(title)) {
    if (!Number.isFinite(descNum)) {
      return null;
    }
    return {
      value: descNum,
      title: "Average Rating",
      scale: 5,
      legacy_title: title,
    };
  }

  if (mid === 10 && /overall\s+sentiment\s+score/i.test(title)) {
    if (!Number.isFinite(descNum)) {
      const pctMatch = /([\d.]+)\s*%/.exec(String(descRaw ?? ""));
      if (!pctMatch) {
        return null;
      }
      const pct = Number(pctMatch[1]);
      if (!Number.isFinite(pct)) {
        return null;
      }
      return {
        value: pct,
        title: "Positive Sentiment Score",
        unit: "%",
        legacy_title: title,
      };
    }
    return {
      value: descNum,
      title: "Positive Sentiment Score",
      unit: "%",
      legacy_title: `${title} ${descNum}%`,
    };
  }

  if (mid === 29 && /overall\s+praise\s+intent\s+score/i.test(title)) {
    if (!Number.isFinite(descNum)) {
      const pctMatch = /([\d.]+)\s*%/.exec(String(descRaw ?? ""));
      if (!pctMatch) {
        return null;
      }
      const pct = Number(pctMatch[1]);
      if (!Number.isFinite(pct)) {
        return null;
      }
      return {
        value: pct,
        title: "Praise Intent Score",
        unit: "%",
        legacy_title: title,
      };
    }
    return {
      value: descNum,
      title: "Praise Intent Score",
      unit: "%",
      legacy_title: `${title} ${descNum}%`,
    };
  }

  return null;
}

export function brandKpiHeroHasDisplayableContent(data, metricId) {
  return resolveBrandKpiHeroHeader(data, metricId) != null;
}

/** Metric 10 header.sentiment_breakdown from API (positive/neutral/mixed/negative + mixed inner). */
export function resolveBrandKpiSentimentBreakdown(header) {
  const raw = header?.sentiment_breakdown;
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const pickBucket = (key) => {
    const node = raw[key];
    if (!node || typeof node !== "object") {
      return null;
    }
    const pct = Number(node.percentage);
    if (!Number.isFinite(pct)) {
      return null;
    }
    const countRaw = node.count;
    const count =
      countRaw != null && countRaw !== "" && Number.isFinite(Number(countRaw))
        ? Number(countRaw)
        : null;
    return { percentage: pct, count };
  };
  const positive = pickBucket("positive");
  const neutral = pickBucket("neutral");
  const negative = pickBucket("negative");
  const mixed = pickBucket("mixed");
  if (!positive && !neutral && !negative && !mixed) {
    return null;
  }
  const innerPct = raw.mixed_inner_percentages;
  const innerCnt = raw.mixed_inner_counts;
  let mixedInner = null;
  if (
    mixed &&
    mixed.count > 0 &&
    innerPct &&
    typeof innerPct === "object"
  ) {
    const pos = Number(innerPct.positive);
    const neu = Number(innerPct.neutral);
    const neg = Number(innerPct.negative);
    if (
      Number.isFinite(pos) ||
      Number.isFinite(neu) ||
      Number.isFinite(neg)
    ) {
      mixedInner = {
        percentages: {
          positive: Number.isFinite(pos) ? pos : 0,
          neutral: Number.isFinite(neu) ? neu : 0,
          negative: Number.isFinite(neg) ? neg : 0,
        },
        counts:
          innerCnt && typeof innerCnt === "object"
            ? {
                positive: Number(innerCnt.positive) || 0,
                neutral: Number(innerCnt.neutral) || 0,
                negative: Number(innerCnt.negative) || 0,
              }
            : null,
      };
    }
  }
  return { positive, neutral, negative, mixed, mixedInner };
}

/** Metric 29 header.intent_breakdown (appraisal/complaint/information/request/mixed + mixed inner). */
export function resolveBrandKpiIntentBreakdown(header) {
  const raw = header?.intent_breakdown;
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const pickBucket = (key) => {
    const node = raw[key];
    if (!node || typeof node !== "object") {
      return null;
    }
    const pct = Number(node.percentage);
    if (!Number.isFinite(pct)) {
      return null;
    }
    const countRaw = node.count;
    const count =
      countRaw != null && countRaw !== "" && Number.isFinite(Number(countRaw))
        ? Number(countRaw)
        : null;
    return { percentage: pct, count };
  };
  const appraisal = pickBucket("appraisal");
  const complaint = pickBucket("complaint");
  const information = pickBucket("information");
  const request = pickBucket("request");
  const mixed = pickBucket("mixed");
  if (!appraisal && !complaint && !information && !request && !mixed) {
    return null;
  }
  const innerPct = raw.mixed_inner_percentages;
  const innerCnt = raw.mixed_inner_counts;
  let mixedInner = null;
  if (
    mixed &&
    mixed.count > 0 &&
    innerPct &&
    typeof innerPct === "object"
  ) {
    const keys = ["appraisal", "complaint", "information", "request"];
    const percentages = {};
    const counts = {};
    let hasInner = false;
    for (const key of keys) {
      const pct = Number(innerPct[key]);
      if (Number.isFinite(pct)) {
        percentages[key] = pct;
        hasInner = true;
      }
      if (innerCnt && typeof innerCnt === "object") {
        counts[key] = Number(innerCnt[key]) || 0;
      }
    }
    if (hasInner) {
      mixedInner = { percentages, counts };
    }
  }
  return { appraisal, complaint, information, request, mixed, mixedInner };
}

export function formatBrandKpiHeroDate(iso, locale) {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const loc = getIntlLocale(locale);
  return d.toLocaleDateString(loc, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatBrandKpiHeroDateRange(start, end, locale) {
  const a = formatBrandKpiHeroDate(start, locale);
  const b = formatBrandKpiHeroDate(end, locale);
  if (a && b) return `${a} – ${b}`;
  return a || b || null;
}
